// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the board, the row and the rivals, as DOM. A tile game wants DOM rather than canvas:
// crisp glyphs at any density, 44px hit targets for free, and a responsive grid that is one CSS
// expression rather than a resize handler.
//
// COLOUR IS NEVER THE ONLY CHANNEL. Every terrain carries a glyph as well as a fill, because six
// hues cannot survive deuteranopia on their own — pine, loch and reed are a green, a blue and a
// violet, and two of those converge. tests/contrast.test.ts asserts a glyph exists for every one.
//
// THE PARITY HATCH IS A RULE MADE VISIBLE, not decoration. A 5×5 minus its centre is exactly 12 dark
// and 12 light, so any empty region whose two colours are unequal can never be filled by anything —
// every doublet covers one of each. Hatching those live turns the last four rounds from guesswork
// into a puzzle you can see, and it is the single change that stops the endgame being a formality.

import { doubletOf } from './deck';
import { GLYPH, PALETTE, SEAT_COLOUR, TERRAIN_KEYS, TERRAIN_NAME } from './palette';
import { EMPTY, MOTTE, mate, spoiledCells, type Demesne, type State } from './rules';
import { escapeHtml } from './dom';
import type { Offer, TurnUi } from './turn';

const glyphSvg = (t: number): string =>
  `<svg class="glyph" viewBox="0 0 24 24" aria-hidden="true"><path d="${GLYPH[TERRAIN_KEYS[t]]}"/></svg>`;

const pips = (k: number): string =>
  k > 0 ? `<span class="pips" aria-hidden="true">${'<i></i>'.repeat(k)}</span>` : '';

/** A terrain cell's inside, used on the demesne, in the row and on a results thumbnail alike. */
export const cellFace = (t: number, k: number): string => `${glyphSvg(t)}${pips(k)}`;

export const terrainLabel = (t: number, k: number): string =>
  `${TERRAIN_NAME[TERRAIN_KEYS[t]]}${k > 0 ? `, ${k} coronet${k === 1 ? '' : 's'}` : ''}`;

export interface BoardHints {
  /** Cells the lifted doublet may be anchored on at the current rotation. */
  legal: Set<number>;
  /** Cells where some OTHER rotation would work — a nudge, not an error. */
  legalRot: Set<number>;
  /** The ghost: [anchorCell, mateCell] with the two terrains, or null. */
  ghost: { a: number; b: number; ta: number; ka: number; tb: number; kb: number; ok: boolean } | null;
  /** Cells placed by the last move, for the drop animation. */
  fresh: Set<number>;
  interactive: boolean;
}

export const emptyHints = (): BoardHints => ({
  legal: new Set(),
  legalRot: new Set(),
  ghost: null,
  fresh: new Set(),
  interactive: false,
});

/** The demesne grid. Rebuilt wholesale — 25 to 49 cells is far cheaper than any diffing scheme. */
export function demesneHtml(d: Demesne, side: number, hints: BoardHints): string {
  const spoiled = spoiledCells(d, side);
  const cells: string[] = [];
  for (let c = 0; c < side * side; c++) {
    const t = d.terr[c];
    const cls = ['cell'];
    let inner = '';
    let label = '';
    if (t === MOTTE) {
      cls.push('motte');
      inner = '<svg class="glyph" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 19h14v-6H5z M5 9h3.2v4H5z M10.4 9h3.2v4h-3.2z M15.8 9h3.2v4h-3.2z"/></svg>';
      label = 'the Motte — matches any land';
    } else if (t === EMPTY) {
      cls.push('empty');
      if (spoiled.has(c)) cls.push('spoil');
      label = 'empty';
    } else {
      cls.push(`t-${TERRAIN_KEYS[t]}`);
      inner = cellFace(t, d.cor[c]);
      label = terrainLabel(t, d.cor[c]);
    }
    if (hints.fresh.has(c)) cls.push('fresh');
    if (hints.interactive && hints.legal.has(c)) cls.push('legal');
    else if (hints.interactive && hints.legalRot.has(c)) cls.push('legalrot');
    const g = hints.ghost;
    if (g && (c === g.a || c === g.b)) {
      const gt = c === g.a ? g.ta : g.tb;
      const gk = c === g.a ? g.ka : g.kb;
      cls.push('ghost', g.ok ? 'ok' : 'bad', `t-${TERRAIN_KEYS[gt]}`);
      inner = cellFace(gt, gk);
      label = `${terrainLabel(gt, gk)}${g.ok ? '' : ' — no match here'}`;
    }
    cells.push(
      `<button class="${cls.join(' ')}" data-c="${c}" type="button" aria-label="${escapeHtml(label)}"${
        hints.interactive ? '' : ' tabindex="-1"'
      }>${inner}</button>`,
    );
  }
  return `<div class="demesne" style="--side:${side}" role="grid" aria-label="your demesne">${cells.join('')}</div>`;
}

/** Build the ghost hint from the current tap state. */
export function ghostOf(s: State, seat: number, ui: TurnUi, ok: boolean): BoardHints['ghost'] {
  if (ui.pick === null || ui.cell === null || seat < 0) return null;
  const doub = s.reveal.includes(ui.pick) ? doubletOf(ui.pick) : null;
  if (!doub) return null;
  const b = mate(s.side, ui.cell, ui.rot);
  if (b < 0) return null;
  return { a: ui.cell, b, ta: doub.a.terrain, ka: doub.a.coronets, tb: doub.b.terrain, kb: doub.b.coronets, ok };
}

/** The face-up row. Always ascending, because the sort order IS the pick-order preview. */
export function offersHtml(list: Offer[], ui: TurnUi, names: string[], burning: boolean): string {
  const rows = list
    .map((o) => {
      const cls = ['offer'];
      if (!o.open) cls.push('gone');
      if (ui.pick === o.n) cls.push('lifted');
      if (o.open && !o.placeable) cls.push('unplaceable');
      const who =
        o.claimedBy >= 0
          ? `<span class="otaken" style="color:${SEAT_COLOUR[o.claimedBy % SEAT_COLOUR.length]}">${escapeHtml(
              names[o.claimedBy] ?? '',
            )}</span>`
          : o.open
            ? ''
            : '<span class="otaken burnt">burnt</span>';
      // THE HIGHEST-LEVERAGE TWENTY LINES IN THE BUILD. The order cost is the whole mechanic and on a
      // phone it is otherwise an inference nobody makes — you tap the top row twelve times and never
      // learn that the fat doublet cost you the initiative. So it is priced, on screen, live.
      // Suppressed on a burn turn: you are choosing what to DESTROY, and telling you what claiming
      // it would buy you is an answer to a question you are not being asked.
      const slot =
        o.slot && !burning
          ? `<span class="oslot">→ ${o.slot.lo === o.slot.hi ? ordinal(o.slot.lo) : `${ordinal(o.slot.lo)}–${ordinal(o.slot.hi)}`} of ${o.slot.of}</span>`
          : '';
      const warn = o.open && !o.placeable ? '<span class="owarn">nowhere to put it</span>' : '';
      return `<button class="${cls.join(' ')}" data-n="${o.n}" type="button" ${o.open ? '' : 'disabled'}
        aria-label="doublet ${o.n}, ${escapeHtml(terrainLabel(o.doub.a.terrain, o.doub.a.coronets))} and ${escapeHtml(
          terrainLabel(o.doub.b.terrain, o.doub.b.coronets),
        )}">
        <span class="onum">${o.n}</span>
        <span class="opair">
          <span class="ocell t-${TERRAIN_KEYS[o.doub.a.terrain]}">${cellFace(o.doub.a.terrain, o.doub.a.coronets)}</span>
          <span class="ocell t-${TERRAIN_KEYS[o.doub.b.terrain]}">${cellFace(o.doub.b.terrain, o.doub.b.coronets)}</span>
        </span>
        <span class="ometa">${slot}${warn}${who}</span>
      </button>`;
    })
    .join('');
  return `<div class="offers${burning ? ' burning' : ''}" role="group" aria-label="the row">${rows}</div>`;
}

const ORDINALS = ['', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];
const ordinal = (n: number): string => ORDINALS[n] ?? `${n}th`;

/** A small read-only demesne — the rival strip and the results screen share it. */
export function miniHtml(d: Demesne, side: number, extraClass = ''): string {
  const cells: string[] = [];
  for (let c = 0; c < side * side; c++) {
    const t = d.terr[c];
    const cls = t === MOTTE ? 'm motte' : t === EMPTY ? 'm empty' : `m t-${TERRAIN_KEYS[t]}${d.cor[c] > 0 ? ' k' : ''}`;
    cells.push(`<i class="${cls}"></i>`);
  }
  return `<div class="mini ${extraClass}" style="--side:${side}" aria-hidden="true">${cells.join('')}</div>`;
}

/**
 * The rival strip. Every other player's demesne and running score, always visible — this game is
 * about what you took from whom, and a board you cannot see is a decision you cannot make.
 */
export function stripHtml(s: State, names: string[], scores: number[], mySeat: number | null, turn: number): string {
  const items: string[] = [];
  for (let p = 0; p < s.players; p++) {
    if (p === mySeat) continue;
    items.push(
      `<button class="rival${turn === p ? ' acting' : ''}" data-seat="${p}" type="button"
         style="--seat:${SEAT_COLOUR[p % SEAT_COLOUR.length]}" aria-label="${escapeHtml(names[p] ?? '')}, ${scores[p]} points">
        <span class="rname">${escapeHtml(names[p] ?? '')}</span>
        ${miniHtml(s.boards[p], s.side)}
        <span class="rscore">${scores[p]}</span>
      </button>`,
    );
  }
  return `<div class="strip">${items.join('')}</div>`;
}

/** The colour a seat is drawn in, exposed so the browser pass can check the real pixels. */
export const seatColour = (p: number): string => SEAT_COLOUR[p % SEAT_COLOUR.length];
export const paletteFor = PALETTE;
